"use client";

// THE FACES of the carrying cells (the auth stage, and the door bench at
// /test/door): what each one says, and the ONE small moving picture that says
// it again without words. The pictures play once, when the cell opens (the
// `.is-open` class on the `.stc` around it), and then rest. Styles live in
// auth-stage.css.

import BrandIcon from "../BrandIcon";
import OrganismMark from "../brand/OrganismMark";
import { featherCellMask } from "./cells";
import type { StageCell } from "./stageCells";

/** the feathered-cell mask every carrying cell wears, written onto `el` as the
 *  CSS vars the cells read (--stc-mask · --stc-kx · --stc-ky) */
export function applyFeather(el: HTMLElement) {
  const m = featherCellMask(0.085);
  el.style.setProperty("--stc-mask", m.url);
  el.style.setProperty("--stc-kx", m.kx.toFixed(4));
  el.style.setProperty("--stc-ky", m.ky.toFixed(4));
}

/** words that arrive one after another — the line being written as you watch */
function Words({ text }: { text: string }) {
  return (
    <>
      {text.split(" ").map((w, i) => (
        <span key={i} className="stc-word" style={{ ["--w" as string]: i }}>
          {w}{" "}
        </span>
      ))}
    </>
  );
}

/** Each face carries ONE small moving picture of what it says, and nothing
 *  more: a tool is a thread to netti with light running both ways (it reads
 *  and writes the same memory); a memory is written word by word and settles
 *  into a cell; a skill lights up in the tools it travels to; netti folds its
 *  cells into itself; a private fact is sealed by a ring that closes round it;
 *  a quote arrives word by word. All of it plays once, as the cell opens, and
 *  rests. */
export function CardFace({ c }: { c: StageCell }) {
  switch (c.kind) {
    case "tool":
      return (
        <>
          <span className="stc-thread">
            <span className="stc-thread__end"><BrandIcon name={c.brand} size={20} /></span>
            <span className="stc-thread__line"><i /><i /></span>
            <span className="stc-thread__end"><OrganismMark tone="gradient" size={20} /></span>
          </span>
          <span className="stc-name">{c.name}</span>
          <span className="stc-line">{c.line}</span>
        </>
      );
    case "memory":
      return (
        <>
          <span className="stc-meta">{c.meta}</span>
          <span className="stc-lead">
            <Words text={c.line} />
            <i className="stc-kept" />
          </span>
        </>
      );
    case "skill":
      return (
        <>
          <span className="stc-meta">skill</span>
          <span className="stc-name">{c.name}</span>
          <span className="stc-line">{c.line}</span>
          <span className="stc-carry">
            {["claude", "cursor", "codex", "gemini"].map((b, i) => (
              <span key={b} className="stc-carry__mark" style={{ ["--w" as string]: i }}>
                <BrandIcon name={b} size={15} />
              </span>
            ))}
            <span className="stc-carry__note">travels with you</span>
          </span>
        </>
      );
    case "netti":
      return (
        <>
          <span className="stc-head">
            <span className="stc-fold">
              {Array.from({ length: c.fold ?? 3 }, (_, i) => (
                <i key={i} style={{ ["--w" as string]: i }} />
              ))}
              <span className="stc-fold__mark"><OrganismMark tone="gradient" size={20} /></span>
            </span>
            <span className="stc-meta">{c.meta}</span>
          </span>
          <span className="stc-lead">{c.line}</span>
        </>
      );
    case "private":
      return (
        <>
          <span className="stc-head">
            <span className="stc-seal">
              <svg viewBox="0 0 28 28" aria-hidden>
                <circle cx="14" cy="14" r="12" pathLength={1} />
              </svg>
              <i />
            </span>
            <span className="stc-meta">{c.meta}</span>
          </span>
          <span className="stc-lead">{c.line}</span>
        </>
      );
    case "quote":
    case "voice":
      return (
        <>
          <span className="stc-qmark">“</span>
          <span className="stc-lead stc-lead--quote">
            <Words text={c.line} />
          </span>
          <span className="stc-meta">
            {c.who}
            {c.kind === "voice" && c.role ? ` · ${c.role}` : ""}
          </span>
        </>
      );
  }
}
